import { GraphQLClient, gql } from 'graphql-request';
import {
  AttendanceStatus,
  CreateEventInput,
  CreateGuestInput,
  CreateInvitationInput,
  CreateInvitationTypeInput,
  Event,
  Guest,
  Invitation,
  InvitationAccessLog,
  InvitationPage,
  InvitationType,
  Mutation,
  PriceDetails,
  PublicCreateResponseInput,
  Query,
  Response,
  SaveInvitationTypeDesignInput,
  UpdateGuestInput,
  UpdateInvitationTypeInput,
  SetEventAdditionalDetailsInput,
  SetEventAdditionalDetailsResult,
  AddEventAdditionalDetailInput,
  AddEventAdditionalDetailResult,
  UpdateEventAdditionalDetailInput,
  UpdateEventAdditionalDetailResult,
  DeleteEventAdditionalDetailInput,
  DeleteEventAdditionalDetailResult,
} from '../_graphql/generated/types';

// The API is ALWAYS same-origin at /graphql.
//
// This used to special-case localhost to `http://localhost:3087` — the old
// NestJS backend's port, which no longer exists. Two problems with that shape,
// beyond the dead port: it made development behave differently from production
// (cross-origin vs same-origin, so CORS and cookie behaviour differed exactly
// where nobody was testing), and it hard-coded a port into the client.
//
// In the monorepo the vite dev server proxies /graphql to the backend, so dev
// and production now take the identical path.
const getApiEndpoint = (): string => `${window.location.protocol}//${window.location.host}/graphql`;

// Create GraphQL client
const apiEndpoint = getApiEndpoint();

// Store for the getToken function
let getTokenFunction: (() => Promise<string | null>) | null = null;

// Function to set the getToken function from React components
export const setAuthTokenGetter = (getToken: () => Promise<string | null>) => {
  getTokenFunction = getToken;
};

// Function to get headers with auth token
const getHeaders = async () => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  
  if (getTokenFunction) {
    try {
      const token = await getTokenFunction();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error getting auth token:', error);
    }
  }
  
  return headers;
};

// Create authenticated client that gets token dynamically
const client = new GraphQLClient(apiEndpoint, {
  requestMiddleware: async (request) => {
    const headers = await getHeaders();
    
    return {
      ...request,
      headers: {
        ...headers,
        ...(request.headers || {}),
      },
    };
  },
  responseMiddleware: (response) => {
    if (response instanceof Error) {
      // Don't log guest limit errors - they're handled in the UI
      const errorMessage = (response as any)?.response?.errors?.[0]?.message || '';
      if (!errorMessage.includes('exceed the event guest limit')) {
        console.error('GraphQL Error Response:', response);
      }
    }
  }
});

// Create a separate client for public operations (no auth headers)
const publicClient = new GraphQLClient(apiEndpoint, {
  headers: {
    'Content-Type': 'application/json',
  },
  responseMiddleware: (response) => {
    if (response instanceof Error) {
      // Don't log guest limit errors - they're handled in the UI
      const errorMessage = (response as any)?.response?.errors?.[0]?.message || '';
      if (!errorMessage.includes('exceed the event guest limit')) {
        console.error('Public GraphQL Error Response:', response);
      }
    }
  }
});

// Guest API
export const guestsApi = {
  getByEvent: async (eventId: string): Promise<Guest[]> => {
    const query = gql`
      query GetGuestsByEvent($eventId: String!) {
        guestsByEvent(eventId: $eventId) {
          id
          name
          email
          phone
          attendance
          eventId
          invitationId
          guestNumber
          primaryGuestId
          createdAt
          updatedAt
          dietaryRequirements
          invitation {
            id
            groupName
            allowedPlusOnes
            token
            invitationType {
              id
              name
            }
            responses {
              id
              message
              createdAt
            }
          }
        }
      }
    `;
    const data = await client.request<Pick<Query, 'guestsByEvent'>>(query, { eventId });
    return data.guestsByEvent;
  },

  getByInvitation: async (invitationId: string): Promise<Guest[]> => {
    const query = gql`
      query GetGuestsByInvitation($invitationId: String!) {
        guestsByInvitation(invitationId: $invitationId) {
          id
          name
          email
          phone
          attendance
          eventId
          invitationId
          primaryGuestId
          dietaryRequirements
          createdAt
          updatedAt
        }
      }
    `;
    const data = await client.request<Pick<Query, 'guestsByInvitation'>>(query, { invitationId });
    return data.guestsByInvitation;
  },

  getByEventAndStatus: async (eventId: string, status: AttendanceStatus): Promise<Guest[]> => {
    const query = gql`
      query GetGuestsByEventAndStatus($eventId: String!, $status: AttendanceStatus) {
        guestsByEventAndStatus(eventId: $eventId, status: $status) {
          id
          name
          email
          phone
          attendance
          eventId
          invitationId
          guestNumber
          primaryGuestId
          createdAt
          updatedAt
          dietaryRequirements
          invitation {
            id
            groupName
            allowedPlusOnes
            token
            invitationType {
              id
              name
            }
            responses {
              id
              message
              createdAt
            }
          }
        }
      }
    `;
    const data = await client.request<Pick<Query, 'guestsByEventAndStatus'>>(query, { eventId, status });
    return data.guestsByEventAndStatus;
  },

  create: async (input: CreateGuestInput): Promise<Guest> => {
    const mutation = gql`
      mutation CreateGuest($input: CreateGuestInput!) {
        createGuest(input: $input) {
          id
          name
          email
          phone
          attendance
          eventId
          invitationId
          guestNumber
          primaryGuestId
          createdAt
          updatedAt
        }
      }
    `;
    const data = await client.request<Pick<Mutation, 'createGuest'>>(mutation, { input });
    return data.createGuest;
  },

  update: async (input: UpdateGuestInput): Promise<Guest> => {
    const mutation = gql`
      mutation UpdateGuest($input: UpdateGuestInput!) {
        updateGuest(input: $input) {
          id
          name
          email
          phone
          attendance
          eventId
          invitationId
          guestNumber
          primaryGuestId
          createdAt
          updatedAt
        }
      }
    `;
    const data = await client.request<Pick<Mutation, 'updateGuest'>>(mutation, { input });
    return data.updateGuest;
  },

  delete: async (id: string): Promise<boolean> => {
    const mutation = gql`
      mutation DeleteGuest($id: ID!) {
        deleteGuest(id: $id)
      }
    `;
    const data = await client.request<Pick<Mutation, 'deleteGuest'>>(mutation, { id });
    return data.deleteGuest;
  },

  addPlusOnes: async (primaryGuestId: string, plusOnes: Array<{ name?: string; email?: string; phone?: string }>): Promise<Guest[]> => {
    const mutation = gql`
      mutation AddPlusOnesToGuest($input: AddPlusOnesToGuestInput!) {
        addPlusOnesToGuest(input: $input) {
          id
          name
          email
          phone
          attendance
          eventId
          invitationId
          guestNumber
          primaryGuestId
          createdAt
          updatedAt
        }
      }
    `;
    const data = await client.request<Pick<Mutation, 'addPlusOnesToGuest'>>(mutation, {
      input: {
        primaryGuestId,
        plusOnes
      }
    });
    return data.addPlusOnesToGuest;
  },
};

// Event API
export const eventsApi = {
  getAll: async (): Promise<Event[]> => {
    const query = gql`
      query GetEvents {
        events {
          id
          code
          eventName
          eventDateTime
          maxGuests
          isPaid
          paidAt
          productCode
          createdAt
          updatedAt
        }
      }
    `;
    const data = await client.request<Pick<Query, 'events'>>(query);
    return data.events;
  },

  getById: async (id: string): Promise<Event | null> => {
    const query = gql`
      query GetEventById($id: ID!) {
        event(id: $id) {
          id
          code
          eventName
          eventDateTime
          maxGuests
          isPaid
          paidAt
          productCode
          createdAt
          updatedAt
          invitations {
            id
            invitationTypeId
            groupName
            allowedPlusOnes
            token
          }
          invitationTypes {
            id
            name
            description
            designCustomizations
          }
        }
      }
    `;
    const data = await client.request<Pick<Query, 'event'>>(query, { id });
    return data.event;
  },

  create: async (input: CreateEventInput): Promise<Event> => {
    const mutation = gql`
      mutation CreateEvent($input: CreateEventInput!) {
        createEvent(input: $input) {
          id
          code
          eventName
          eventDateTime
          maxGuests
          isPaid
          paidAt
          productCode
          createdAt
          updatedAt
        }
      }
    `;
    const data = await client.request<Pick<Mutation, 'createEvent'>>(mutation, { input });
    return data.createEvent;
  },

  getAdditionalDetails: async (eventId: string) => {
    const query = gql`
      query GetEventAdditionalDetails($eventId: String!) {
        getEventAdditionalDetails(eventId: $eventId) {
          eventId
          additionalDetails {
            key
            value
            displayOnPage
          }
        }
      }
    `;
    const data = await client.request<Pick<Query, 'getEventAdditionalDetails'>>(query, { eventId });
    return data.getEventAdditionalDetails;
  },

  setAdditionalDetails: async (input: { eventId: string; additionalDetails: Array<{ key: string; value: string; displayOnPage?: boolean }> }) => {
    const mutation = gql`
      mutation SetEventAdditionalDetails($input: SetEventAdditionalDetailsInput!) {
        setEventAdditionalDetails(input: $input) {
          eventId
          additionalDetails {
            key
            value
            displayOnPage
          }
        }
      }
    `;
    const data = await client.request<Pick<Mutation, 'setEventAdditionalDetails'>>(mutation, { input });
    return data.setEventAdditionalDetails;
  },

  addAdditionalDetail: async (input: { eventId: string; key: string; value: string; displayOnPage?: boolean }) => {
    const mutation = gql`
      mutation AddEventAdditionalDetail($input: AddEventAdditionalDetailInput!) {
        addEventAdditionalDetail(input: $input) {
          success
          message
        }
      }
    `;
    const data = await client.request<Pick<Mutation, 'addEventAdditionalDetail'>>(mutation, { input });
    return data.addEventAdditionalDetail;
  },

  updateAdditionalDetail: async (input: { eventId: string; key: string; value: string; displayOnPage?: boolean }) => {
    const mutation = gql`
      mutation UpdateEventAdditionalDetail($input: UpdateEventAdditionalDetailInput!) {
        updateEventAdditionalDetail(input: $input) {
          success
          message
        }
      }
    `;
    const data = await client.request<Pick<Mutation, 'updateEventAdditionalDetail'>>(mutation, { input });
    return data.updateEventAdditionalDetail;
  },

  deleteAdditionalDetail: async (input: { eventId: string; key: string }) => {
    const mutation = gql`
      mutation DeleteEventAdditionalDetail($input: DeleteEventAdditionalDetailInput!) {
        deleteEventAdditionalDetail(input: $input) {
          success
          message
        }
      }
    `;
    const data = await client.request<Pick<Mutation, 'deleteEventAdditionalDetail'>>(mutation, { input });
    return data.deleteEventAdditionalDetail;
  },
};

// Invitation Type API
export const invitationTypesApi = {
  getByEvent: async (eventId: string): Promise<InvitationType[]> => {
    const query = gql`
      query GetInvitationTypesByEvent($eventId: String!) {
        invitationTypesByEvent(eventId: $eventId) {
          id
          eventId
          name
          description
          designCustomizations
          createdAt
          updatedAt
        }
      }
    `;
    const data = await client.request<Pick<Query, 'invitationTypesByEvent'>>(query, { eventId });
    return data.invitationTypesByEvent;
  },

  create: async (input: CreateInvitationTypeInput): Promise<InvitationType> => {
    const mutation = gql`
      mutation CreateInvitationType($input: CreateInvitationTypeInput!) {
        createInvitationType(input: $input) {
          id
          eventId
          name
          description
          designCustomizations
          createdAt
          updatedAt
        }
      }
    `;
    const data = await client.request<Pick<Mutation, 'createInvitationType'>>(mutation, { input });
    return data.createInvitationType;
  },

  update: async (input: UpdateInvitationTypeInput): Promise<InvitationType> => {
    const mutation = gql`
      mutation UpdateInvitationType($input: UpdateInvitationTypeInput!) {
        updateInvitationType(input: $input) {
          id
          eventId
          name
          description
          designCustomizations
          createdAt
          updatedAt
        }
      }
    `;
    const data = await client.request<Pick<Mutation, 'updateInvitationType'>>(mutation, { input });
    return data.updateInvitationType;
  },

  saveDesign: async (input: SaveInvitationTypeDesignInput): Promise<InvitationType> => {
    const mutation = gql`
      mutation SaveInvitationTypeDesign($input: SaveInvitationTypeDesignInput!) {
        saveInvitationTypeDesign(input: $input) {
          id
          eventId
          name
          description
          designCustomizations
          createdAt
          updatedAt
        }
      }
    `;
    const data = await client.request<Pick<Mutation, 'saveInvitationTypeDesign'>>(mutation, { input });
    return data.saveInvitationTypeDesign;
  },

  delete: async (id: string): Promise<boolean> => {
    const mutation = gql`
      mutation DeleteInvitationType($id: ID!) {
        deleteInvitationType(id: $id)
      }
    `;
    const data = await client.request<Pick<Mutation, 'deleteInvitationType'>>(mutation, { id });
    return data.deleteInvitationType;
  },
};

// Invitation API
export const invitationsApi = {
  create: async (input: CreateInvitationInput): Promise<Invitation> => {
    const mutation = gql`
      mutation CreateInvitation($input: CreateInvitationInput!) {
        createInvitation(input: $input) {
          id
          eventId
          invitationTypeId
          groupName
          allowedPlusOnes
          token
          createdAt
          updatedAt
        }
      }
    `;
    const data = await client.request<Pick<Mutation, 'createInvitation'>>(mutation, { input });
    return data.createInvitation;
  },

  send: async (invitationId: string) => {
    const mutation = gql`
      mutation SendInvitation($invitationId: String!) {
        sendInvitation(invitationId: $invitationId) {
          invitationId
          sentToGuests {
            guestId
            guestName
            deliveryMethod
            success
            errorMessage
          }
        }
      }
    `;
    const data = await client.request<Pick<Mutation, 'sendInvitation'>>(mutation, { invitationId });
    return data.sendInvitation;
  },
};

// Public Invitation API (conditionally authenticated)
export const publicInvitationsApi = {
  getByToken: async (token: string, eventCode: string): Promise<InvitationPage | null> => {
    const query = gql`
      query GetInvitationByToken($token: String!, $eventCode: String!) {
        invitationByToken(token: $token, eventCode: $eventCode) {
          invitation {
            id
            groupName
            allowedPlusOnes
            guests {
              id
              name
              email
              phone
              attendance
              dietaryRequirements
              primaryGuestId
              guestNumber
            }
          }
          event {
            id
            code
            eventName
            eventDateTime
            additionalDetails {
              key
              value
              displayOnPage
            }
          }
          invitationType {
            id
            name
            description
            designCustomizations
            rsvpByDate
          }
          design
          rsvpDate
        }
      }
    `;
    
    // Create a client with conditional auth headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    // Only add auth header if we have a token getter function (user is logged in)
    if (getTokenFunction) {
      try {
        const authToken = await getTokenFunction();
        if (authToken) {
          headers['Authorization'] = `Bearer ${authToken}`;
        }
      } catch (error) {
        // If there's an error getting the token, continue without auth
        console.debug('No auth token available for getByToken request');
      }
    }
    
    const conditionalClient = new GraphQLClient(apiEndpoint, {
      headers,
      responseMiddleware: (response) => {
        if (response instanceof Error) {
          // Don't log guest limit errors - they're handled in the UI
          const errorMessage = (response as any)?.response?.errors?.[0]?.message || '';
          if (!errorMessage.includes('exceed the event guest limit')) {
            console.error('GraphQL Error Response:', response);
          }
        }
      }
    });
    
    const data = await conditionalClient.request<Pick<Query, 'invitationByToken'>>(query, { token, eventCode });
    return data.invitationByToken;
  },

  submitResponse: async (input: PublicCreateResponseInput): Promise<Response> => {
    const mutation = gql`
      mutation SubmitPublicResponse($input: PublicCreateResponseInput!) {
        submitPublicResponse(input: $input) {
          id
          invitationId
          createdAt
          updatedAt
        }
      }
    `;
    const data = await publicClient.request<Pick<Mutation, 'submitPublicResponse'>>(mutation, { input });
    return data.submitPublicResponse;
  },
};

// Access Logs API
export const accessLogsApi = {
  getByEvent: async (eventId: string): Promise<InvitationAccessLog[]> => {
    const query = gql`
      query GetInvitationAccessLogsByEvent($eventId: String!) {
        invitationAccessLogsByEvent(eventId: $eventId) {
          id
          invitationId
          userId
          userAgent
          ipAddress
          accessedAt
        }
      }
    `;
    const data = await client.request<Pick<Query, 'invitationAccessLogsByEvent'>>(query, { eventId });
    return data.invitationAccessLogsByEvent;
  },
};

// Response API
export const responsesApi = {
  getAll: async (): Promise<Response[]> => {
    const query = gql`
      query GetResponses {
        responses {
          id
          invitationId
          message
          createdAt
          updatedAt
        }
      }
    `;
    const data = await client.request<Pick<Query, 'responses'>>(query);
    return data.responses;
  },
};

// Event Details Extraction API
export const eventDetailsApi = {
  parseFromDesign: async (customizations: any): Promise<{
    eventName: string | null;
    eventDate: string | null;
    rawExtractedData: any;
  }> => {
    const query = gql`
      query ParseEventDetails($customizations: JSON!) {
        parseEventDetailsFromDesign(customizations: $customizations) {
          eventName
          eventDate
          rawExtractedData
        }
      }
    `;
    
    // Use public client since this query is marked as @Public()
    const data = await publicClient.request<{
      parseEventDetailsFromDesign: {
        eventName: string | null;
        eventDate: string | null;
        rawExtractedData: any;
      };
    }>(query, { customizations });
    
    return data.parseEventDetailsFromDesign;
  },
};

// Payments API
export const paymentsApi = {
  createPaymentIntent: async (eventId: string, productCode: string): Promise<{ clientSecret: string }> => {
    const mutation = gql`
      mutation CreatePaymentIntent($eventId: String!, $productCode: String!) {
        createPaymentIntent(eventId: $eventId, productCode: $productCode) {
          clientSecret
        }
      }
    `;

    const data = await client.request<{
      createPaymentIntent: { clientSecret: string };
    }>(mutation, { eventId, productCode });

    return data.createPaymentIntent;
  },

  confirmPaymentSuccess: async (eventId: string, paymentIntentId: string): Promise<{
    success: boolean;
    event: Event;
    message?: string;
  }> => {
    const mutation = gql`
      mutation ConfirmPaymentSuccess($eventId: String!, $paymentIntentId: String!) {
        confirmPaymentSuccess(eventId: $eventId, paymentIntentId: $paymentIntentId) {
          success
          event {
            id
            code
            eventName
            eventDateTime
            maxGuests
            isPaid
            paidAt
            productCode
          }
          message
        }
      }
    `;

    const data = await client.request<{
      confirmPaymentSuccess: {
        success: boolean;
        event: Event;
        message?: string;
      };
    }>(mutation, { eventId, paymentIntentId });

    return data.confirmPaymentSuccess;
  },

  getPriceDetails: async (productCode: string): Promise<{
    priceId: string;
    amount: number;
    currency: string;
    description: string;
  }> => {
    const query = gql`
      query GetPriceDetails($productCode: String!) {
        getPriceDetails(productCode: $productCode) {
          priceId
          amount
          currency
          description
        }
      }
    `;

    const data = await client.request<{
      getPriceDetails: {
        priceId: string;
        amount: number;
        currency: string;
        description: string;
      };
    }>(query, { productCode });

    return data.getPriceDetails;
  },
};